<template>
    <v-container fluid class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
            <v-col cols="12" md="8">
                <v-card class="elevation-2">
                    <v-card-title>
                        Andamento Fatturato Mensile
                        <v-spacer />
                        <v-btn-toggle v-model="tipoVista" mandatory dense>
                            <v-btn small value="mensile">Mensile</v-btn>
                            <v-btn small value="cumulato">Cumulato</v-btn>
                        </v-btn-toggle>
                    </v-card-title>
                    <v-card-text>
                        <mixed-chart :data="graficoTendenza" :options="opzioniTendenza" :key="keyTendenza" />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="4">
                <v-card class="elevation-2 mb-3">
                    <v-card-title class="text-subtitle-1">Previsione Fine Anno {{ new Date().getFullYear()
                        }}</v-card-title>
                    <v-card-text>
                        <div class="text-center">
                            <div class="text-h4 primary--text font-weight-bold mb-2">
                                € {{ previsioneFineAnno.toLocaleString('it-IT', { minimumFractionDigits: 2 }) }}
                            </div>
                            <v-progress-linear :value="progressoTarget" color="primary" height="25" class="mb-2">
                                <strong>{{ progressoTarget.toFixed(1) }}%</strong>
                            </v-progress-linear>
                            <div class="text-caption">
                                Target: € {{ targetAnno.toLocaleString('it-IT') }}
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card class="elevation-2">
                    <v-card-title class="text-subtitle-1">Statistiche Periodo</v-card-title>
                    <v-card-text>
                        <v-list dense>
                            <v-list-item v-for="stat in statistichePeriodo" :key="stat.etichetta">
                                <v-list-item-content>
                                    <v-list-item-subtitle>{{ stat.etichetta }}</v-list-item-subtitle>
                                    <v-list-item-title>{{ stat.valore }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row class="ma-0 pa-0 mt-3">
            <v-col cols="12" md="6">
                <v-card class="elevation-2">
                    <v-card-title>Confronto Ultimi 5 Anni</v-card-title>
                    <v-card-text>
                        <bar-chart :data="graficoConfrontoAnni" :options="opzioniBarre" :key="keyAnni" />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <v-card class="elevation-2">
                    <v-card-title>Stagionalità (Media Ultimi 3 Anni)</v-card-title>
                    <v-card-text>
                        <line-chart :data="graficoStagionalita" :options="opzioniLinea" :key="keyStagione" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { get_dati_grafico_mese, get_dati_grafico } from "@/api/api.documenti.js";
import MixedChart from "@/components/chart/MixedChart.js";
import BarChart from "@/components/chart/BarChart.js";
import LineChart from "@/components/chart/LineChart.js";
import mixinCharts from "@/components/chart/mixinCharts.js";

export default {
    name: "TabAndamento",

    components: {
        MixedChart,
        BarChart,
        LineChart
    },

    mixins: [mixinCharts],

    props: {
        filtri: { type: Object, default: () => ({}) },
        aggiornaDati: { type: Number, default: 0 }
    },

    data: () => ({
        tipoVista: 'mensile',

        graficoTendenza: {},
        graficoConfrontoAnni: {},
        graficoStagionalita: {},

        opzioniTendenza: {},
        opzioniBarre: {},
        opzioniLinea: {},

        keyTendenza: 0,
        keyAnni: 0,
        keyStagione: 0,

        previsioneFineAnno: 0,
        targetAnno: 250000,
        progressoTarget: 0,

        statistichePeriodo: [
            { etichetta: 'Media mensile', valore: '€ 0' },
            { etichetta: 'Miglior mese', valore: '-' },
            { etichetta: 'Peggior mese', valore: '-' },
            { etichetta: 'Varianza', valore: '0%' }
        ]
    }),

    methods: {
        async caricaDatiTendenza() {
            try {
                let filtriCorrente = Object.assign({}, this.filtri);
                filtriCorrente.anno = this.filtri.anno;

                let filtriPrecedente = Object.assign({}, this.filtri);
                filtriPrecedente.anno = this.filtri.anno - 1;

                let resCorrente = await get_dati_grafico_mese(filtriCorrente);
                let resPrecedente = await get_dati_grafico_mese(filtriPrecedente);

                const mesi = this.$i2kFunctions.getShortMonths().map(m => m.charAt(0).toUpperCase() + m.slice(1));

                let datiCorrente = resCorrente.data.datasets[0].data;
                let datiPrecedente = resPrecedente.data.datasets[0].data;

                if (this.tipoVista === 'cumulato') {
                    datiCorrente = this.calcolaCumulato(datiCorrente);
                    datiPrecedente = this.calcolaCumulato(datiPrecedente);
                }

                this.graficoTendenza = {
                    labels: mesi,
                    datasets: [
                        {
                            label: this.filtri.anno.toString(),
                            data: datiCorrente,
                            type: 'bar',
                            backgroundColor: 'rgba(76, 175, 80, 0.7)',
                            borderColor: 'rgba(76, 175, 80, 1)',
                            borderWidth: 2
                        },
                        {
                            label: (this.filtri.anno - 1).toString(),
                            data: datiPrecedente,
                            type: 'line',
                            fill: false,
                            borderColor: 'rgba(255, 152, 0, 1)',
                            borderWidth: 2,
                            pointRadius: 4,
                            pointBackgroundColor: 'rgba(255, 152, 0, 1)'
                        }
                    ]
                };

                this.calcolaStatistiche(resCorrente.data.datasets[0].data, mesi);
                this.keyTendenza++;

            } catch (error) {
                console.error("Errore caricamento tendenza:", error);
            }
        },

        async caricaDatiConfrontoAnni() {
            try {
                let filtriAnni = Object.assign({}, this.filtri);
                filtriAnni.mese = -1;
                filtriAnni.meseStr = "";

                let res = await get_dati_grafico(filtriAnni);
                this.graficoConfrontoAnni = res.data;
                this.keyAnni++;

            } catch (error) {
                console.error("Errore caricamento confronto anni:", error);
            }
        },

        async caricaDatiStagionalita() {
            try {
                const mesi = this.$i2kFunctions.getShortMonths().map(m => m.charAt(0).toUpperCase() + m.slice(1));
                let totaliMesi = new Array(12).fill(0);
                let contatoriMesi = new Array(12).fill(0);

                // Calcola media ultimi 3 anni
                for (let i = 0; i < 3; i++) {
                    let filtriAnno = Object.assign({}, this.filtri);
                    filtriAnno.anno = this.filtri.anno - i;

                    let res = await get_dati_grafico_mese(filtriAnno);
                    res.data.datasets[0].data.forEach((valore, idx) => {
                        if (valore > 0) {
                            totaliMesi[idx] += valore;
                            contatoriMesi[idx]++;
                        }
                    });
                }

                const medieMesi = totaliMesi.map((tot, idx) =>
                    contatoriMesi[idx] > 0 ? tot / contatoriMesi[idx] : 0
                );

                this.graficoStagionalita = {
                    labels: mesi,
                    datasets: [{
                        label: 'Media Stagionale',
                        data: medieMesi,
                        fill: true,
                        backgroundColor: 'rgba(63, 81, 181, 0.2)',
                        borderColor: 'rgba(63, 81, 181, 1)',
                        borderWidth: 2,
                        tension: 0.4
                    }]
                };

                this.keyStagione++;

            } catch (error) {
                console.error("Errore caricamento stagionalità:", error);
            }
        },

        calcolaCumulato(dati) {
            let cumulato = [];
            let somma = 0;
            dati.forEach(val => {
                somma += val;
                cumulato.push(somma);
            });
            return cumulato;
        },

        calcolaStatistiche(dati, mesi) {
            const valoriValidi = dati.filter(v => v > 0);

            if (valoriValidi.length === 0) return;

            const media = valoriValidi.reduce((a, b) => a + b, 0) / valoriValidi.length;
            const max = Math.max(...valoriValidi);
            const min = Math.min(...valoriValidi);
            const indiceMax = dati.indexOf(max);
            const indiceMin = dati.indexOf(min);

            // Calcola varianza
            const sommaQuadrati = valoriValidi.reduce((acc, val) => acc + Math.pow(val - media, 2), 0);
            const varianza = Math.sqrt(sommaQuadrati / valoriValidi.length);
            const coefficienteVariazione = (varianza / media) * 100;

            this.statistichePeriodo[0].valore = '€ ' + this.$functions.numberWithCommas(media.toFixed(2));
            this.statistichePeriodo[1].valore = mesi[indiceMax] + ' (€ ' + this.$functions.numberWithCommas(max.toFixed(0)) + ')';
            this.statistichePeriodo[2].valore = mesi[indiceMin] + ' (€ ' + this.$functions.numberWithCommas(min.toFixed(0)) + ')';
            this.statistichePeriodo[3].valore = '± ' + coefficienteVariazione.toFixed(1) + '%';

            // Calcola previsione
            const meseCorrente = new Date().getMonth();
            const totaleAnnoCorrente = dati.reduce((a, b) => a + b, 0);
            const mesiTrascorsi = Math.max(1, valoriValidi.length);
            this.previsioneFineAnno = (totaleAnnoCorrente / mesiTrascorsi) * 12;
            this.progressoTarget = (totaleAnnoCorrente / this.targetAnno) * 100;
        },

        caricaTuttiDati() {
            this.caricaDatiTendenza();
            this.caricaDatiConfrontoAnni();
            this.caricaDatiStagionalita();
            this.$emit('caricaDati');
        }
    },

    mounted() {
        this.opzioniTendenza = this.getOptionsMixedChart();
        this.opzioniBarre = this.getOptionsMixedChart();
        this.opzioniLinea = this.getOptionsMixedChart();
        this.caricaTuttiDati();
    },

    watch: {
        aggiornaDati() {
            this.caricaTuttiDati();
        },

        tipoVista() {
            this.caricaDatiTendenza();
        }
    }
}
</script>